import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS as LMS_ENDPOINTS } from '../../../Utils/Endpoints';
import { API_BASE } from '../../../../../api';
import { Models } from '../../../Utils/ModelsLMS';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import axios from 'axios';
import Modal from './EstudiantesPremiosModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../Shared/AlertDialog';
import { isSessionSuperAdmin, getSessionEmpresaId } from '../../../../../utils/SessionUser';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = "Catálogo de Compras de Premios";
const endpoint = LMS_ENDPOINTS.estudiantesPremios;
const endpointById = LMS_ENDPOINTS.estudiantesPremiosById;
const modelo = Models.estudiantesPremios;

const maps = {
    mapCreate: (form) => ({
        idEstudiante: Number(form.idEstudiante),
        idPremio: Number(form.idPremio),
        costo: Number(form.costo),
        fechaCompra: form.fechaCompra,
        activo: form.activo !== undefined ? form.activo : true,
    }),
    mapUpdate: (id, form) => ({
        idEstudiantePremios: Number(id),
        idEstudiante: Number(form.idEstudiante),
        idPremio: Number(form.idPremio),
        costo: Number(form.costo),
        fechaCompra: form.fechaCompra,
        activo: form.activo !== undefined ? form.activo : true,
    }),
};

export default function Catalog() {
    const { puedeCrear, puedeEditar, puedeEliminar, puedeExportar } = useResourcePermissions('Estudiantes Premios');
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [estudiantes, setEstudiantes] = useState([]);
    const [premios, setPremios] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [personas, setPersonas] = useState([]);
    const [query, setQuery] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setModalOpen(false);
        setDeleteOpen(false);
        setAlertConfig({ open: true, message, variant, title });
    };

    const editorRef = useRef(null);

    useEffect(() => {
        if (modalOpen && editorRef.current) {
            editorRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [modalOpen, selectedId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [resEP, resEst, resPrem, resEmp, resPer] = await Promise.all([
                axios.get(endpoint),
                axios.get(LMS_ENDPOINTS.estudiante),
                axios.get(LMS_ENDPOINTS.catPremios),
                axios.get(`${API_BASE}/Empleados`),
                axios.get(`${API_BASE}/Personas`),
            ]);
            setRows((resEP.data || []).map((x) => modelo(x)));
            setEstudiantes((resEst.data || []).map(x => Models.estudiante(x)));
            setPremios((resPrem.data || []).map(x => Models.premios(x)));
            setEmpleados((resEmp.data || []).map(x => Models.empleado(x)));
            setPersonas((resPer.data || []).map(x => Models.personas(x)));
        } catch (e) {
            console.error(e);
            triggerAlert('Error al cargar información');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const getEmpleadoNombre = (idEmpleado) => {
        const emp = empleados.find(e => Number(e.idEmpleado) === Number(idEmpleado));
        if (!emp) return `Empleado ${idEmpleado}`;
        const per = personas.find(p => Number(p.idPersona) === Number(emp.idPersona));
        return per ? `${per.nombre} ${per.apellidoPaterno} ${per.apellidoMaterno || ''}`.trim() : `Empleado ${idEmpleado}`;
    };

    const getEstudianteNombre = (idEstudiante) => {
        const est = estudiantes.find(e => Number(e.idEstudiante) === Number(idEstudiante));
        if (!est) return `Estudiante ${idEstudiante}`;
        return est.nickname || getEmpleadoNombre(est.idEmpleado);
    };

    const getPremioNombre = (idPremio) => {
        const prem = premios.find(p => Number(p.idPremio) === Number(idPremio));
        return prem ? prem.nombre : `Premio ${idPremio}`;
    };

    const filtered = useMemo(() => {
        const sessionEmpresaId = getSessionEmpresaId();
        const q = query.trim().toLowerCase();
        let res = rows;

        if (sessionEmpresaId && !isSessionSuperAdmin()) {
            res = res.filter(r => r.idEmpresa === sessionEmpresaId);
        }

        if (!q) return res;

        return res.filter((r) => {
            const estName = getEstudianteNombre(r.idEstudiante).toLowerCase();
            const premName = getPremioNombre(r.idPremio).toLowerCase();
            const cost = String(r.costo || '');

            return estName.includes(q) ||
                premName.includes(q) ||
                cost.includes(q);
        });
    }, [rows, query, estudiantes, premios, empleados, personas]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    const handleNew = () => {
        setModalMode('new');
        setSelectedId(null);
        setEditData(null);
        setModalOpen(true);
    };

    const handleEdit = async (id) => {
        try {
            setLoading(true);
            const res = await axios.get(endpointById(id));
            const modelData = modelo(res.data);
            setEditData(modelData);
            setModalMode('edit');
            setSelectedId(id);
            setModalOpen(true);
        } catch (e) {
            console.error(e);
            triggerAlert('Error al cargar detalle');
        } finally {
            setLoading(false);
        }
    };

    const askDelete = (id, name) => {
        setDeleteTarget({ id, name });
        setDeleteOpen(true);
    };

    const doDelete = async () => {
        setDeleteOpen(false);
        setLoading(true);
        try {
            await axios.delete(endpointById(deleteTarget.id));
            triggerAlert('Compra de premio eliminada con éxito', 'success', 'Éxito');
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al eliminar');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (payload, mode) => {
        try {
            setLoading(true);
            if (mode === 'new') {
                await axios.post(endpoint, maps.mapCreate(payload));
            } else {
                await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
            }
            setModalOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            type="button"
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >{label}</button>
    );

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i);
    })();

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        try {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } catch {
            return '—';
        }
    };

    // Exportar a Excel (CSV)
    const downloadExcel = () => {
        if (!rows || rows.length === 0) {
            triggerAlert("No hay datos para exportar.", "warning", "Atención");
            return;
        }

        const headers = ["Estudiante", "Premio", "Costo", "Fecha de Compra"];
        const csvRows = [
            headers.join(","),
            ...filtered.map((r) => {
                return [
                    `"${r.estudianteNombre || getEstudianteNombre(r.idEstudiante)}"`,
                    `"${r.premioNombre || getPremioNombre(r.idPremio)}"`,
                    `"${r.costo} monedas"`,
                    `"${formatDate(r.fechaCompra)}"`
                ].join(",");
            })
        ];

        const csvString = "\uFEFF" + csvRows.join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Premios_Estudiantes_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar">
                    <h2 className="roles-title">{nombreTabla}</h2>

                    <input
                        className="roles-search"
                        placeholder="Buscar por estudiante, premio o costo…"
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                    {puedeExportar && (
                        <button className="roles-btn" onClick={downloadExcel} title="Descargar Excel" style={{ marginLeft: '10px' }}>
                            📥 Descargar Excel
                        </button>
                    )}
                    {/* Creación gestionada desde la página de Estudiantes/Premios — no se expone aquí
                    {puedeCrear && (
                      <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
                    )}
                    */}
                </div>

                <div className="roles-table-wrap">
                    <div className="roles-table-scroller">
                        <table className="roles-table">
                            <thead>
                                <tr>
                                    <th>Estudiante</th>
                                    <th>Premio</th>
                                    <th>Costo</th>
                                    <th>Fecha de Compra</th>
                                    {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                                    {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map((r) => (
                                    <tr key={r.idEstudiantePremios}>
                                        <td><strong>{r.estudianteNombre || getEstudianteNombre(r.idEstudiante)}</strong></td>
                                        <td>{r.premioNombre || getPremioNombre(r.idPremio)}</td>
                                        <td>{r.costo} monedas</td>
                                        <td>{formatDate(r.fechaCompra)}</td>
                                        {puedeEditar && (
                                            <td style={{ textAlign: 'center' }}>
                                                <button
                                                    type="button"
                                                    className="rk-iconbtn rk-iconbtn--edit"
                                                    onClick={() => handleEdit(r.idEstudiantePremios)}
                                                    aria-label="Editar"
                                                    title="Editar"
                                                >
                                                    <img src={sigmaEdit} alt="" className="rk-ico" />
                                                </button>
                                            </td>
                                        )}
                                        {puedeEliminar && (
                                            <td style={{ textAlign: 'center' }}>
                                                <button
                                                    type="button"
                                                    className="rk-iconbtn rk-iconbtn--del"
                                                    onClick={() => {
                                                        const name = `${getEstudianteNombre(r.idEstudiante)} - ${getPremioNombre(r.idPremio)}`;
                                                        askDelete(r.idEstudiantePremios, name);
                                                    }}
                                                    aria-label="Borrar"
                                                    title="Borrar"
                                                >
                                                    <img src={sigmaDelete} alt="" className="rk-ico" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                                {!loading && paged.length === 0 && (
                                    <tr><td colSpan={4 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="roles-pagination">
                    <div className="roles-pagination-left">
                        {filtered.length > 0
                            ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                            : <span>0 resultados</span>}
                    </div>
                    <div className="roles-pagination-right">
                        <PageBtn
                            label="‹"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                        />
                        {pageNumbers.map(n => (
                            <PageBtn
                                key={n}
                                label={n}
                                onClick={() => setPage(n)}
                                active={n === page}
                            />
                        ))}
                        <PageBtn
                            label="›"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                        />
                    </div>
                </div>

                {modalOpen && (
                    <div className="roles-editor-slot" ref={editorRef}>
                        <Modal
                            open={modalOpen}
                            mode={modalMode}
                            data={editData}
                            estudiantes={estudiantes}
                            premios={premios}
                            empleados={empleados}
                            personas={personas}
                            inline
                            onClose={() => setModalOpen(false)}
                            onSubmit={handleSubmit}
                        />
                    </div>
                )}
            </div>

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
